import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import { SpriteIcon } from '@/components/SpriteIcon';
import { AuthCard, AuthCardLinks, AuthErrorSummary } from './AuthCard';
import { AuthField } from './AuthField';
import { SubmitButton } from './SubmitButton';
import { forgotPasswordSchema, type ForgotPasswordValues } from './schemas';
import { useServerValidation } from './useServerValidation';

const FIELDS = ['email'] as const;

/**
 * Replaces `Pages/Auth/Forgotpassword.cshtml`.
 *
 * Two states in one component, as the Razor page's `@if (Model.Submitted)` was.
 * The confirmation is unconditional because the endpoint is: it answers 204
 * whether or not the address belongs to an account, so that the form cannot be
 * used to find out which addresses are registered. Only a transport or
 * antiforgery failure gets an error here.
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);

  const schema = useMemo(() => forgotPasswordSchema(t), [t]);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  });

  const { formErrors, clearFormErrors, applyServerErrors } = useServerValidation(setError, FIELDS);

  const onSubmit = handleSubmit(async (values) => {
    clearFormErrors();

    try {
      await api.post<void>('/api/auth/forgot-password', values);
      setSubmitted(true);
    } catch (error) {
      applyServerErrors(error);
    }
  });

  if (submitted) {
    return (
      <AuthCard
        aboveTitle={<SpriteIcon name="check-circle" size={48} className="sa-icon text-success mb-3" />}
        title={t('auth:Auth.SentEmail', 'Check Your Email')}
        subtitle={t(
          'auth:Auth.CheckEmailMessage',
          'If an account exists for that address, we have sent a link to reset your password.',
        )}
        footer={<AuthCardLinks />}
      >
        <div className="d-grid gap-2">
          <Link
            to="/auth-login"
            className="btn btn-primary btn-lg d-flex align-items-center justify-content-center"
          >
            <SpriteIcon name="arrow-left" className="sa-icon me-2" />
            {t('auth:Auth.BackToLogin', 'Back to Login')}
          </Link>
        </div>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title={t('auth:Auth.ForgotPassword', 'Forgot Password?')}
      subtitle={t(
        'auth:Auth.ForgotPasswordMessage',
        'Enter your email address and we will send you a link to reset your password',
      )}
      footer={<AuthCardLinks />}
    >
      <AuthErrorSummary errors={formErrors} />

      <form onSubmit={onSubmit} noValidate>
        <AuthField
          {...register('email')}
          label={t('auth:Auth.Email', 'Email Address')}
          type="email"
          large
          autoComplete="email"
          autoFocus
          placeholder={t('auth:Auth.EnterEmail', 'Enter your email')}
          error={errors.email?.message}
        />

        <div className="d-grid gap-2">
          <SubmitButton
            icon="send"
            label={t('auth:Auth.ResetPassword', 'Reset Password')}
            pending={isSubmitting}
          />
        </div>
      </form>

      <div className="text-center mt-4">
        <p className="mb-0 text-muted">
          <Link to="/auth-login" className="fw-bold text-decoration-none">
            {t('auth:Auth.BackToLogin', 'Back to Login')}
          </Link>
        </p>
      </div>
    </AuthCard>
  );
}
